// Admin user types and what each may see. The sidebar still follows each admin's saved
// `permissions` (copied from `usertypedefaultpermissions` when the user is created);
// these rules sit on top, so a role never gets a menu it must not have, whatever its
// saved copy says or an admin ticks on Edit User. The API applies the same rules to its
// money endpoints (controllers/requireAdminUser.js in myspotsaver-api); keep the two in
// step.

import { withRevenueMenu } from "./revenueMenu";

export const USER_TYPES = [
    { value: "ADMIN", label: "Admin" },
    { value: "FINANCIALMANAGER", label: "Financial Manager" },
    { value: "MANAGER", label: "Manager" },
];

// The menus that show money: what visitors and clients paid, what SpotSaver earned,
// what venues are paid out.
export const FINANCE_MENUS = ["Revenue", "Payouts", "Transactions", "Subscriptions"];

// Managers add and edit clients and their locations in the field, so they see no
// financial data.
const BLOCKED_MENUS = {
    MANAGER: FINANCE_MENUS,
};

// Who may refund a visitor's payment from Transactions.
const REFUND_TYPES = ["ADMIN", "FINANCIALMANAGER"];

const normalize = (userType) => String(userType || "").trim().toUpperCase();
// An admin with no user type predates them and keeps the full access they always had.
const roleOf = (userType) => normalize(userType) || "ADMIN";
const sameMenu = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

export const userTypeLabel = (userType) =>
    USER_TYPES.find((t) => t.value === normalize(userType))?.label || String(userType || "");

export const isMenuBlocked = (userType, menu) =>
    (BLOCKED_MENUS[roleOf(userType)] || []).some((m) => sameMenu(m, menu));

/**
 * Whether the signed-in admin may see financial figures outside the finance pages
 * (Dashboard revenue, a client's payouts). False until their user data has loaded.
 */
export const mayViewFinance = (userData) =>
    Array.isArray(userData?.permissions) && !FINANCE_MENUS.some((m) => isMenuBlocked(userData?.userType, m));

export const canRefund = (userType) => REFUND_TYPES.includes(roleOf(userType));

/** Adding users and editing other users' accounts. Everyone keeps their own Profile. */
export const canManageUsers = (userType) => roleOf(userType) === "ADMIN";

// What the API answers when the caller's session is fine but their user type may not do
// what they asked. Not "Invalid token or Expired", so it must not open the sign-in popup.
export const ROLE_REFUSED = "Not allowed for your user type";

const switchOff = (entry) => {
    const off = { ...entry, display: false, enable: false };
    if (entry?.dashboard) {
        off.dashboard = Object.fromEntries(Object.entries(entry.dashboard)
            .map(([key, action]) => [key, { ...action, display: false, enable: false }]));
    }
    if (Array.isArray(entry?.submenus)) off.submenus = entry.submenus.map(switchOff);
    return off;
};

/** `permissions` with every menu the user type may not have switched off, actions too. */
export const withRoleLimits = (userType, permissions) => {
    if (!Array.isArray(permissions)) return permissions;
    return permissions.map((p) => (isMenuBlocked(userType, p?.menu) ? switchOff(p) : p));
};

/** The menus the signed-in admin's sidebar lists, in saved order. */
export const visibleMenus = (userData) =>
    withRoleLimits(userData?.userType, withRevenueMenu(userData?.permissions))
        .filter((p) => p?.display === true);

export const canOpenMenu = (userData, menu) => visibleMenus(userData).some((p) => sameMenu(p.menu, menu));
